import { Outlet, NavLink } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { LogOut, FileBarChart2, Settings, Wallet } from 'lucide-react';
import { logout, selectUser } from '../store/authSlice';

/**
 * Application chrome for authenticated users.
 *
 * Layout: header across the top, fixed sidebar on the left, main content
 * area on the right rendered by {@link Outlet} from the child route.
 *
 * Sidebar is fixed (always visible) per current product decision. If more
 * top-level nav items appear later, just add them to the list below.
 */
export default function DashboardLayout() {
    const dispatch = useDispatch();
    const user = useSelector(selectUser);

    return (
        <div className="min-h-screen bg-slate-50 flex flex-col">
            <header className="bg-white border-b border-slate-200">
                <div className="px-6 py-4 flex items-center justify-between">
                    <h1 className="font-display text-xl font-semibold text-slate-900">
                        Commissioner
                    </h1>
                    <div className="flex items-center gap-4">
                        {user?.email && (
                            <span className="text-sm text-slate-500 hidden sm:inline">
                                {user.email}
                            </span>
                        )}
                        <button
                            onClick={() => dispatch(logout())}
                            className="flex items-center gap-2 px-3 py-1.5 text-sm text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-md"
                        >
                            <LogOut size={14} />
                            Sign out
                        </button>
                    </div>
                </div>
            </header>

            <div className="flex flex-1">
                <aside className="w-60 bg-white border-r border-slate-200 flex-shrink-0">
                    <nav className="p-4">
                        <p className="px-3 mb-2 text-xs font-medium uppercase tracking-wider text-slate-400">
                            Reports
                        </p>
                        <ul className="space-y-1">
                            <li>
                                <SidebarLink
                                    to="/commission-reports"
                                    icon={<FileBarChart2 size={16} />}
                                    label="Commission reports"
                                />
                            </li>
                            <li>
                                <SidebarLink
                                    to="/payouts"
                                    icon={<Wallet size={16} />}
                                    label="Payouts"
                                />
                            </li>
                        </ul>
                        <p className="px-3 mb-2 mt-6 text-xs font-medium uppercase tracking-wider text-slate-400">
                            Setup
                        </p>
                        <ul className="space-y-1">
                            <li>
                                <SidebarLink
                                    to="/configuration"
                                    icon={<Settings size={16} />}
                                    label="Configuration"
                                />
                            </li>
                        </ul>
                    </nav>
                </aside>

                <main className="flex-1 min-w-0">
                    <Outlet />
                </main>
            </div>
        </div>
    );
}

/**
 * Sidebar nav item with active-route styling. Uses `end={false}` so the
 * commission-reports link stays highlighted when on a /commission-reports/...
 * child route too.
 */
function SidebarLink({ to, icon, label }) {
    return (
        <NavLink
            to={to}
            className={({ isActive }) =>
                [
                    'flex items-center gap-2.5 px-3 py-2 text-sm rounded-md transition-colors',
                    isActive
                        ? 'bg-brand-50 text-brand-700 font-medium'
                        : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100',
                ].join(' ')
            }
        >
            {icon}
            {label}
        </NavLink>
    );
}